/* What an entitlement answer means.
 *
 * Pulled out of js/licensing.js for the reason queue.js was pulled out of
 * sync.js: the file it came from cannot be imported without a Supabase config
 * and a window, and the decision below is the one thing in this whole feature
 * that must not be wrong.
 *
 * ── THE RULE ────────────────────────────────────────────────────────────────
 *
 * A console that does not KNOW what a salon has bought licenses everything.
 *
 * Not as a convenience and not as a fallback that somebody will tidy up later:
 * this is a commercial control shipped in a bundle on a tablet, so the cost of
 * it being defeated is a salon using a module they have not paid for, and the
 * cost of it being wrong the other way is a front desk that cannot run a shift.
 * There is no version of this product in which the second is the better failure.
 *
 * "Does not know" covers a project where the licensing schema was never
 * applied, a salon whose wifi is down, a station nobody has signed in yet, and
 * the second and a half before the first answer lands. Only an explicit answer
 * from the database — a plan, or a term that has ended — switches anything off.
 */

// The state before anything is known. `known: false` is what licenses
// everything; every other field is here so a caller can read it without
// checking whether it exists.
var UNKNOWN = {
  known: false,
  planId: null,
  planName: null,
  features: [],
  seats: null,
  expiresAt: null,
  status: 'unknown'
};

function empty() {
  return {
    known: UNKNOWN.known,
    planId: UNKNOWN.planId,
    planName: UNKNOWN.planName,
    features: [],
    seats: UNKNOWN.seats,
    expiresAt: UNKNOWN.expiresAt,
    status: UNKNOWN.status
  };
}

/* One row of `tenant_entitlements()`, as this app holds it.
 *
 * Null is a real answer here and is not the same as no answer: a salon with no
 * grant comes back as `state: 'none'` with no features, and that IS knowing.
 * A row is only rejected when it is not a row at all.
 *
 * `features` is taken as given rather than validated against a list of ids this
 * build understands. The database is the authority on what was granted, and a
 * console that dropped an id it did not recognise would be a console that could
 * never be sold a feature without being rebuilt first.
 */
function fromRow(row) {
  if (!row || typeof row !== 'object') return null;
  return {
    known: true,
    planId: row.plan_id || null,
    planName: row.plan_name || null,
    features: Array.isArray(row.features) ? row.features.slice() : [],
    seats: row.seats == null ? null : Number(row.seats),
    expiresAt: row.expires_at || null,
    // The three the database returns. Anything else is treated as the most
    // restrictive of them, because an unrecognised state is not a licence.
    status: row.state === 'active' || row.state === 'lapsed' ? row.state : 'none'
  };
}

// A cached value from a previous session, which may have been written by an
// older build. Same shape, same scepticism: anything that is not an object with
// a feature list is not an answer, and not-an-answer licenses everything.
function fromCache(value) {
  if (!value || typeof value !== 'object') return null;
  if (!Array.isArray(value.features)) return null;
  return {
    known: true,
    planId: value.planId || null,
    planName: value.planName || null,
    features: value.features.slice(),
    seats: value.seats == null ? null : Number(value.seats),
    expiresAt: value.expiresAt || null,
    status: value.status === 'active' || value.status === 'lapsed' ? value.status : 'none'
  };
}

/* The question the rest of the console asks.
 *
 * The first line is the important one. Everything after it is a membership
 * test — there is no expiry arithmetic here, deliberately: whether a term has
 * ended is decided by the database, in `private.license_resolve()`, and a
 * lapsed licence arrives with an empty feature list. Two clocks disagreeing
 * about when a licence ended is exactly the bug this avoids by not having a
 * second clock.
 */
/* The exceptions to the rule at the top of this file, and deliberately few.
 * `dreamcloud` is the owner's view of the pipeline behind the salon, with fixes
 * that write to advertising accounts. `optimizer` changes who a client's booking
 * is with, and a salon without it is meant to see nothing change. No shift needs
 * either, so the cost of failing open (a station listing it in the switcher while
 * the answer is on its way) buys nothing. Until the licence is known they are not
 * offered. The edge functions are what actually refuse; this only keeps the
 * modules out of view. Every other module still fails open. */
var WITHHELD_UNTIL_KNOWN = { dreamcloud: true, optimizer: true };

function entitles(state, feature) {
  if (!state || !state.known) return !(feature && WITHHELD_UNTIL_KNOWN[feature] === true);
  if (!feature) return true;
  return state.features.indexOf(feature) !== -1;
}

export const licence = {
  empty: empty,
  fromRow: fromRow,
  fromCache: fromCache,
  entitles: entitles
};
